import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import { toast } from "sonner";
import { 
  Sparkles, Plus, Pencil, Trash2, ChevronRight, ChevronDown,
  ClipboardList, Package, Users, Calendar, Check, X,
  GripVertical, Droplets, SprayCanIcon, Pipette, FileUp, Download, FileText, Briefcase
} from "lucide-react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { TaskDialog, AreaDialog, ProdottoPuliziaDialog } from "../components/pulizie/ProgrammaPulizieDialogs";
import { Checkbox } from "../components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { useConfirm } from "../hooks/useConfirm";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FREQUENZE = [
  { value: "dopo_uso", label: "Dopo ogni utilizzo" },
  { value: "giornaliero", label: "Giornaliero" },
  { value: "settimanale", label: "Settimanale" },
  { value: "mensile", label: "Mensile" },
];

const GIORNI_SETTIMANA = [
  { value: 0, label: "Lun" },
  { value: 1, label: "Mar" },
  { value: 2, label: "Mer" },
  { value: 3, label: "Gio" },
  { value: 4, label: "Ven" },
  { value: 5, label: "Sab" },
  { value: 6, label: "Dom" },
];

const TIPI_PRODOTTO = [
  { value: "detergente", label: "Detergente", icon: Droplets },
  { value: "sanificante", label: "Sanificante", icon: SprayCanIcon },
  { value: "multiuso", label: "Multiuso", icon: Pipette },
];

const REPARTI = [
  { value: "tutti", label: "Tutti" },
  { value: "cucina", label: "Cucina" },
  { value: "sala", label: "Sala" },
  { value: "laboratorio", label: "Laboratorio" },
];

const ProgrammaPulizie = () => {
  const { getAuthHeaders, localeCorrente } = useAuth();
  const confirm = useConfirm();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("task");
  
  // Data
  const [aree, setAree] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [prodotti, setProdotti] = useState([]);
  const [operatori, setOperatori] = useState([]);
  
  // Expanded areas
  const [expandedAreas, setExpandedAreas] = useState({});
  
  // Dialogs
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [areaDialogOpen, setAreaDialogOpen] = useState(false);
  const [prodottoDialogOpen, setProdottoDialogOpen] = useState(false);
  
  // Forms
  const [editTask, setEditTask] = useState(null);
  const [editArea, setEditArea] = useState(null);
  const [editProdotto, setEditProdotto] = useState(null);
  
  const [taskForm, setTaskForm] = useState({
    area_id: "",
    nome: "",
    descrizione: "",
    frequenza: "giornaliero",
    giorni_settimana: [],
    giorno_mese: null,
    prodotti_ids: [],
    operatori_ids: [],
    assegnazione_tipo: "operatori",
    reparto: null
  });
  
  const [areaForm, setAreaForm] = useState({ nome: "", colore: "#84cc16" });
  const [prodottoForm, setProdottoForm] = useState({ nome: "", tipo: "", note: "", scheda_tecnica_url: "", scheda_tecnica_nome: "" });
  const [uploadingScheda, setUploadingScheda] = useState(false);
  
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (localeCorrente?.id) {
      fetchData();
    }
  }, [localeCorrente?.id]);

  const fetchData = async () => {
    if (!localeCorrente?.id) return;

    // Init aree predefinite (non-blocking: non blocca il fetch se fallisce)
    try {
      await axios.post(`${API}/aree-pulizia/init?locale_id=${localeCorrente.id}`, {}, { headers: getAuthHeaders() });
    } catch (e) {
      console.warn("Init aree-pulizia fallita (non critico):", e);
    }

    try {
      const [areeRes, tasksRes, prodottiRes, utentiRes] = await Promise.all([
        axios.get(`${API}/aree-pulizia?locale_id=${localeCorrente.id}`, { headers: getAuthHeaders() }),
        axios.get(`${API}/task-pulizia?locale_id=${localeCorrente.id}`, { headers: getAuthHeaders() }),
        axios.get(`${API}/prodotti-pulizia?locale_id=${localeCorrente.id}`, { headers: getAuthHeaders() }),
        axios.get(`${API}/utenti`, { headers: getAuthHeaders() })
      ]);
      
      setAree(areeRes.data);
      setTasks(tasksRes.data);
      setProdotti(prodottiRes.data);
      
      // Filtra solo operatori (cucina/sala) del locale corrente
      const ops = utentiRes.data.filter(u => 
        u.ruolo !== "admin" && 
        (u.locali_ids || []).includes(localeCorrente.id)
      );
      setOperatori(ops);
      
      // Espandi tutte le aree di default
      const expanded = {};
      areeRes.data.forEach(a => expanded[a.id] = true);
      setExpandedAreas(expanded);
    } catch (error) {
      toast.error("Errore nel caricamento dei dati");
    } finally {
      setLoading(false);
    }
  };

  // === TASK ===
  const openTaskDialog = (task = null, areaId = null) => {
    if (task) {
      setEditTask(task);
      setTaskForm({
        area_id: task.area_id,
        nome: task.nome,
        descrizione: task.descrizione || "",
        frequenza: task.frequenza,
        giorni_settimana: task.giorni_settimana || [],
        giorno_mese: task.giorno_mese,
        prodotti_ids: task.prodotti_ids || [],
        operatori_ids: task.operatori_ids || [],
        assegnazione_tipo: task.assegnazione_tipo || "operatori",
        reparto: task.reparto || null
      });
    } else {
      setEditTask(null);
      setTaskForm({
        area_id: areaId || (aree[0]?.id || ""),
        nome: "",
        descrizione: "",
        frequenza: "giornaliero",
        giorni_settimana: [],
        giorno_mese: null,
        prodotti_ids: [],
        operatori_ids: [],
        assegnazione_tipo: "operatori",
        reparto: null
      });
    }
    setTaskDialogOpen(true);
  };

  const handleTaskSubmit = async (e) => {
    e.preventDefault();
    if (!taskForm.nome.trim() || !taskForm.area_id) {
      toast.error("Nome e Area sono obbligatori");
      return;
    }
    if (taskForm.assegnazione_tipo === "operatori" && (!taskForm.operatori_ids || taskForm.operatori_ids.length === 0)) {
      toast.error("Seleziona almeno un operatore a cui assegnare la task");
      return;
    }
    if (taskForm.assegnazione_tipo === "reparto" && !taskForm.reparto) {
      toast.error("Seleziona il reparto a cui assegnare la task");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        locale_id: localeCorrente.id,
        ...taskForm
      };

      if (editTask) {
        await axios.put(`${API}/task-pulizia/${editTask.id}`, taskForm, { headers: getAuthHeaders() });
        toast.success("Task aggiornato");
      } else {
        await axios.post(`${API}/task-pulizia`, payload, { headers: getAuthHeaders() });
        toast.success("Task creato");
      }
      
      setTaskDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTask = async (id) => {
    if (!await confirm("Sei sicuro di voler eliminare questo task?")) return;
    
    try {
      await axios.delete(`${API}/task-pulizia/${id}`, { headers: getAuthHeaders() });
      toast.success("Task eliminato");
      fetchData();
    } catch (error) {
      toast.error("Errore nell'eliminazione");
    }
  };

  // === AREE ===
  const openAreaDialog = (area = null) => {
    setEditArea(area);
    setAreaForm(area ? { nome: area.nome, colore: area.colore } : { nome: "", colore: "#84cc16" });
    setAreaDialogOpen(true);
  };

  const handleAreaSubmit = async (e) => {
    e.preventDefault();
    if (!areaForm.nome.trim()) {
      toast.error("Nome obbligatorio");
      return;
    }

    setSaving(true);
    try {
      if (editArea) {
        await axios.put(
          `${API}/aree-pulizia/${editArea.id}?nome=${encodeURIComponent(areaForm.nome)}&colore=${encodeURIComponent(areaForm.colore)}`,
          {},
          { headers: getAuthHeaders() }
        );
        toast.success("Area aggiornata");
      } else {
        await axios.post(`${API}/aree-pulizia`, {
          locale_id: localeCorrente.id,
          nome: areaForm.nome,
          colore: areaForm.colore
        }, { headers: getAuthHeaders() });
        toast.success("Area creata");
      }
      
      setAreaDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteArea = async (id) => {
    if (!await confirm("Sei sicuro di voler eliminare questa area?")) return;
    
    try {
      await axios.delete(`${API}/aree-pulizia/${id}`, { headers: getAuthHeaders() });
      toast.success("Area eliminata");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nell'eliminazione");
    }
  };

  // === PRODOTTI ===
  const openProdottoDialog = (prodotto = null) => {
    setEditProdotto(prodotto);
    setProdottoForm(prodotto 
      ? { nome: prodotto.nome, tipo: prodotto.tipo, note: prodotto.note || "", scheda_tecnica_url: prodotto.scheda_tecnica_url || "", scheda_tecnica_nome: prodotto.scheda_tecnica_nome || "" } 
      : { nome: "", tipo: "detergente", note: "", scheda_tecnica_url: "", scheda_tecnica_nome: "" }
    );
    setProdottoDialogOpen(true);
  };

  // Upload scheda tecnica
  const handleSchedaUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Verifica tipo file
    if (!file.type.includes("pdf") && !file.type.includes("image")) {
      toast.error("Formato non supportato. Usa PDF o immagini.");
      return;
    }
    
    // Verifica dimensione (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File troppo grande (max 10MB)");
      return;
    }
    
    // Se stiamo modificando un prodotto esistente, upload diretto
    if (editProdotto) {
      setUploadingScheda(true);
      try {
        const reader = new FileReader();
        reader.onload = async () => {
          const base64 = reader.result;
          await axios.post(`${API}/prodotti-pulizia/${editProdotto.id}/scheda-tecnica`, {
            file_data: base64,
            file_name: file.name
          }, { headers: getAuthHeaders() });
          
          setProdottoForm(prev => ({ ...prev, scheda_tecnica_url: "uploaded", scheda_tecnica_nome: file.name }));
          toast.success("Scheda tecnica caricata");
          fetchData();
        };
        reader.readAsDataURL(file);
      } catch (error) {
        toast.error("Errore nel caricamento");
      } finally {
        setUploadingScheda(false);
      }
    } else {
      // Se stiamo creando, salviamo temporaneamente nel form
      const reader = new FileReader();
      reader.onload = () => {
        setProdottoForm(prev => ({ 
          ...prev, 
          scheda_tecnica_data: reader.result,
          scheda_tecnica_nome: file.name 
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDeleteScheda = async () => {
    if (!editProdotto) {
      setProdottoForm(prev => ({ ...prev, scheda_tecnica_url: "", scheda_tecnica_nome: "", scheda_tecnica_data: null }));
      return;
    }
    
    try {
      await axios.delete(`${API}/prodotti-pulizia/${editProdotto.id}/scheda-tecnica`, { headers: getAuthHeaders() });
      setProdottoForm(prev => ({ ...prev, scheda_tecnica_url: "", scheda_tecnica_nome: "" }));
      toast.success("Scheda tecnica rimossa");
      fetchData();
    } catch (error) {
      toast.error("Errore nella rimozione");
    }
  };

  const handleProdottoSubmit = async (e) => {
    e.preventDefault();
    if (!prodottoForm.nome.trim() || !prodottoForm.tipo || prodottoForm.tipo === "__legacy__") {
      toast.error("Nome e tipologia sono obbligatori");
      return;
    }

    setSaving(true);
    try {
      if (editProdotto) {
        await axios.put(
          `${API}/prodotti-pulizia/${editProdotto.id}?nome=${encodeURIComponent(prodottoForm.nome)}&tipo=${prodottoForm.tipo}&note=${encodeURIComponent(prodottoForm.note || "")}`,
          {},
          { headers: getAuthHeaders() }
        );
        toast.success("Prodotto aggiornato");
      } else {
        const res = await axios.post(`${API}/prodotti-pulizia`, {
          locale_id: localeCorrente.id,
          ...prodottoForm
        }, { headers: getAuthHeaders() });
        // Aggiorna lista immediatamente con la risposta del backend
        if (res.data && res.data.id) {
          setProdotti(prev => [...prev, res.data]);
        }
        toast.success("Prodotto creato");
      }
      
      setProdottoDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteProdotto = async (id) => {
    if (!await confirm("Sei sicuro di voler eliminare questo prodotto?")) return;
    
    try {
      await axios.delete(`${API}/prodotti-pulizia/${id}`, { headers: getAuthHeaders() });
      toast.success("Prodotto eliminato");
      fetchData();
    } catch (error) {
      toast.error("Errore nell'eliminazione");
    }
  };

  // === HELPERS ===
  const handleDownloadFile = async (fileUrlPath, fileName) => {
    const token = (window.localStorage && window.localStorage.getItem("token")) || "";
    const baseUrl = `${API.replace('/api', '')}${fileUrlPath}`;
    const url = token ? `${baseUrl}${baseUrl.includes('?') ? '&' : '?'}t=${encodeURIComponent(token)}` : baseUrl;
    try {
      const res = await fetch(url);
      const blob = await res.blob();
      const file = new File([blob], fileName, { type: blob.type });
      if (navigator.canShare?.({ files: [file] })) {
        await navigator.share({ files: [file], title: fileName });
        return;
      }
    } catch (e) {
      if (e?.name === 'AbortError') return;
    }
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const getTasksByArea = (areaId) => tasks.filter(t => t.area_id === areaId);
  const getFrequenzaLabel = (freq) => FREQUENZE.find(f => f.value === freq)?.label || freq;
  const getProdottoNome = (id) => prodotti.find(p => p.id === id)?.nome || "";
  const getOperatoreNome = (id) => {
    const op = operatori.find(o => o.id === id);
    return op?.nome || op?.email || op?.telefono || "";
  };

  const toggleGiornoSettimana = (giorno) => {
    const current = taskForm.giorni_settimana || [];
    if (current.includes(giorno)) {
      setTaskForm({ ...taskForm, giorni_settimana: current.filter(g => g !== giorno) });
    } else {
      setTaskForm({ ...taskForm, giorni_settimana: [...current, giorno].sort() });
    }
  };

  const toggleProdotto = (id) => {
    const current = taskForm.prodotti_ids || [];
    if (current.includes(id)) {
      setTaskForm({ ...taskForm, prodotti_ids: current.filter(p => p !== id) });
    } else {
      setTaskForm({ ...taskForm, prodotti_ids: [...current, id] });
    }
  };

  const toggleOperatore = (id) => {
    const current = taskForm.operatori_ids || [];
    if (current.includes(id)) {
      setTaskForm({ ...taskForm, operatori_ids: current.filter(o => o !== id) });
    } else {
      setTaskForm({ ...taskForm, operatori_ids: [...current, id] });
    }
  };

  if (loading) {
    return (
      <div className="loading-state">
        <div className="text-muted-custom">Caricamento...</div>
      </div>
    );
  }

  return (
    <div className="page-container" data-testid="programma-pulizie-page">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title flex items-center gap-2">
            <Sparkles className="h-6 w-6 text-cyan-400" />
            Piano Pulizie
          </h1>
          <p className="page-subtitle">Configura il piano di sanificazione HACCP</p>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="tabs-pill-list">
          <TabsTrigger value="task" className="flex items-center gap-2">
            <ClipboardList className="h-4 w-4" />
            <span className="hidden sm:inline">Task Pulizie</span>
          </TabsTrigger>
          <TabsTrigger value="prodotti" className="flex items-center gap-2">
            <Package className="h-4 w-4" />
            <span className="hidden sm:inline">Prodotti</span>
          </TabsTrigger>
          <TabsTrigger value="aree" className="flex items-center gap-2">
            <GripVertical className="h-4 w-4" />
            <span className="hidden sm:inline">Aree</span>
          </TabsTrigger>
        </TabsList>

        {/* TAB TASK */}
        <TabsContent value="task" className="mt-4 space-y-4">
          <div className="flex justify-end">
            <Button onClick={() => openTaskDialog()} className="btn-primary">
              <Plus className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Nuovo Task</span>
            </Button>
          </div>

          {aree.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Sparkles className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>Nessuna area configurata</p>
            </div>
          ) : (
            <div className="space-y-3">
              {aree.map((area) => {
                const areaTasks = getTasksByArea(area.id);
                const isExpanded = expandedAreas[area.id];
                
                return (
                  <div key={area.id} className="bg-card rounded-2xl border border-border overflow-hidden">
                    {/* Area Header */}
                    <button
                      onClick={() => setExpandedAreas({ ...expandedAreas, [area.id]: !isExpanded })}
                      className="w-full flex items-center gap-3 p-4 hover:bg-card transition-colors"
                    >
                      <div 
                        className="w-4 h-4 rounded-full flex-shrink-0" 
                        style={{ backgroundColor: area.colore }}
                      />
                      <span className="font-bold text-white flex-1 text-left">{area.nome}</span>
                      <span className="text-muted-foreground text-sm">{areaTasks.length} task</span>
                      {isExpanded ? (
                        <ChevronDown className="h-5 w-5 text-muted-foreground" />
                      ) : (
                        <ChevronRight className="h-5 w-5 text-muted-foreground" />
                      )}
                    </button>
                    
                    {/* Tasks List */}
                    {isExpanded && (
                      <div className="border-t border-border">
                        {areaTasks.length === 0 ? (
                          <div className="p-4 text-center text-white/30 text-sm">
                            Nessun task in questa area
                          </div>
                        ) : (
                          areaTasks.map((task) => (
                            <div 
                              key={task.id}
                              className="flex items-center gap-3 p-4 border-b border-white/5 last:border-b-0 hover:bg-card"
                            >
                              <div className="flex-1 min-w-0">
                                <h4 className="text-foreground font-medium">{task.nome}</h4>
                                <div className="flex flex-wrap gap-2 mt-1">
                                  <span className="text-xs px-2 py-0.5 bg-cyan-500/20 rounded-full text-cyan-600 dark:text-cyan-400">
                                    {getFrequenzaLabel(task.frequenza)}
                                  </span>
                                  {task.frequenza === "settimanale" && task.giorni_settimana?.length > 0 && (
                                    <span className="text-xs px-2 py-0.5 bg-muted/50 rounded-full text-foreground">
                                      {task.giorni_settimana.map(g => GIORNI_SETTIMANA[g]?.label).join(", ")}
                                    </span>
                                  )}
                                  {/* Assegnazione */}
                                  {task.assegnazione_tipo === "reparto" && task.reparto ? (
                                    <span className="text-xs px-2 py-0.5 bg-violet-500/20 rounded-full text-violet-600 dark:text-violet-400 flex items-center gap-1">
                                      <Briefcase className="h-3 w-3" />
                                      {task.reparto === "tutti" ? "Tutti" : 
                                       task.reparto === "cucina" ? "Cucina" :
                                       task.reparto === "sala" ? "Sala" :
                                       task.reparto === "laboratorio" ? "Laboratorio" : task.reparto}
                                    </span>
                                  ) : task.operatori_ids?.length > 0 ? (
                                    <span className="text-xs px-2 py-0.5 bg-violet-500/20 rounded-full text-violet-600 dark:text-violet-400 flex items-center gap-1">
                                      <Users className="h-3 w-3" />
                                      {task.operatori_ids.length} operatori
                                    </span>
                                  ) : null}
                                </div>
                              </div>
                              <div className="flex gap-1">
                                <Button 
                                  variant="ghost" 
                                  size="icon" 
                                  onClick={() => openTaskDialog(task)}
                                  className="h-8 w-8 text-muted-foreground hover:text-foreground"
                                >
                                  <Pencil className="h-4 w-4" />
                                </Button>
                                <Button 
                                  variant="ghost" 
                                  size="icon" 
                                  onClick={() => handleDeleteTask(task.id)}
                                  className="h-8 w-8 text-red-400 hover:text-red-300 hover:bg-red-500/10"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </div>
                          ))
                        )}
                        <button
                          onClick={() => openTaskDialog(null, area.id)}
                          className="w-full p-3 text-center text-cyan-400 hover:bg-cyan-500/10 text-sm font-medium"
                        >
                          <Plus className="h-4 w-4 inline mr-1" />
                          Aggiungi task a {area.nome}
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* TAB PRODOTTI */}
        <TabsContent value="prodotti" className="mt-4 space-y-4">
          <div className="flex justify-end">
            <Button onClick={() => openProdottoDialog()} className="btn-primary">
              <Plus className="h-4 w-4 mr-2" />
              Nuovo Prodotto
            </Button>
          </div>

          {prodotti.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Package className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>Nessun prodotto configurato</p>
            </div>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {prodotti.map((prodotto) => {
                const tipoInfo = TIPI_PRODOTTO.find(t => t.value === prodotto.tipo);
                const TipoIcon = tipoInfo?.icon || Droplets;
                const tipoLabel = tipoInfo?.label || prodotto.tipo;
                return (
                  <div 
                    key={prodotto.id}
                    className="bg-card rounded-2xl border border-border overflow-hidden"
                  >
                    {/* Header con icona e azioni */}
                    <div className="p-4 flex items-start gap-3">
                      <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0">
                        <TipoIcon className="h-5 w-5 text-primary" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-semibold text-foreground truncate">{prodotto.nome}</h4>
                        <span className="text-xs text-muted-foreground">{tipoLabel}</span>
                      </div>
                      <div className="flex gap-1 flex-shrink-0">
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          onClick={() => openProdottoDialog(prodotto)}
                          className="h-8 w-8 text-muted-foreground hover:text-foreground hover:bg-muted"
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          onClick={() => handleDeleteProdotto(prodotto.id)}
                          className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    
                    {/* Note e Scheda Tecnica */}
                    {(prodotto.note || prodotto.scheda_tecnica_url) && (
                      <div className="px-4 pb-4 pt-0 space-y-2">
                        {prodotto.note && (
                          <p className="text-sm text-muted-foreground line-clamp-2">{prodotto.note}</p>
                        )}
                        {prodotto.scheda_tecnica_url && (
                          <button 
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDownloadFile(prodotto.scheda_tecnica_url, prodotto.scheda_tecnica_nome || `Scheda_${prodotto.nome}.pdf`);
                            }}
                            className="inline-flex items-center gap-2 px-3 py-2 bg-primary text-white hover:bg-primary/90 text-xs font-semibold rounded-xl transition-colors active:scale-95 shadow-sm"
                          >
                            <Download className="h-4 w-4" />
                            Scheda Tecnica
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* TAB AREE */}
        <TabsContent value="aree" className="mt-4 space-y-4">
          <div className="flex justify-end">
            <Button onClick={() => openAreaDialog()} className="btn-primary">
              <Plus className="h-4 w-4 mr-2" />
              Nuova Area
            </Button>
          </div>

          <div className="grid gap-3">
            {aree.map((area, idx) => (
              <div 
                key={area.id}
                className="flex items-center gap-3 p-4 bg-card rounded-2xl border border-border"
              >
                <div 
                  className="w-8 h-8 rounded-full flex items-center justify-center text-black font-bold text-sm"
                  style={{ backgroundColor: area.colore }}
                >
                  {idx + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="text-white font-medium">{area.nome}</h4>
                  {area.predefinita && (
                    <span className="text-xs text-muted-foreground">Area predefinita</span>
                  )}
                </div>
                <div className="flex gap-1">
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    onClick={() => openAreaDialog(area)}
                    className="h-8 w-8 text-muted-foreground hover:text-foreground"
                    data-testid={`edit-area-${area.id}`}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    onClick={() => handleDeleteArea(area.id)}
                    className="h-8 w-8 text-red-400 hover:text-red-300 hover:bg-red-500/10"
                    data-testid={`delete-area-${area.id}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      {/* Dialog Task */}
      <TaskDialog
        open={taskDialogOpen}
        onOpenChange={setTaskDialogOpen}
        editTask={editTask}
        taskForm={taskForm}
        setTaskForm={setTaskForm}
        onSubmit={handleTaskSubmit}
        saving={saving}
        aree={aree}
        prodotti={prodotti}
        operatori={operatori}
        toggleGiornoSettimana={toggleGiornoSettimana}
        toggleProdotto={toggleProdotto}
        toggleOperatore={toggleOperatore}
      />

      {/* Dialog Area */}
      <AreaDialog
        open={areaDialogOpen}
        onOpenChange={setAreaDialogOpen}
        editArea={editArea}
        areaForm={areaForm}
        setAreaForm={setAreaForm}
        onSubmit={handleAreaSubmit}
        saving={saving}
      />

      {/* Dialog Prodotto */}
      <ProdottoPuliziaDialog
        open={prodottoDialogOpen}
        onOpenChange={setProdottoDialogOpen}
        editProdotto={editProdotto}
        prodottoForm={prodottoForm}
        setProdottoForm={setProdottoForm}
        onSubmit={handleProdottoSubmit}
        saving={saving}
        onSchedaUpload={handleSchedaUpload}
        onDeleteScheda={handleDeleteScheda}
        uploadingScheda={uploadingScheda}
        API={API}
      />
    </div>
  );
};

export default ProgrammaPulizie;
